import { isBefore, isFuture, startOfDay } from "date-fns";
import validator from "validator";
import { z } from "zod";

import { PROFESSIONAL_REFERENCE_STATUS, VALIDATION_MESSAGES } from "../constants";

/**
 * Name validation regex
 * Allows: letters, spaces, hyphens, apostrophes
 * Disallows: numbers, special characters
 */
const NAME_REGEX = /^[A-Za-z\s\-']+$/;

/**
 * Check if employer name contains "clipboard" or "cbh" (case-insensitive)
 */
function isClipboardEmployer(value: string): boolean {
  const lowerCase = value.toLowerCase();
  return lowerCase.includes("cbh") || lowerCase.includes("clipboard");
}

/**
 * Zod schema for reference form data
 */
export const referenceFormSchema = z
  .object({
    employerName: z
      .string()
      .trim()
      .min(1, VALIDATION_MESSAGES.REQUIRED)
      .min(3, VALIDATION_MESSAGES.EMPLOYER_MIN_LENGTH)
      .refine((value) => !isClipboardEmployer(value), {
        message: VALIDATION_MESSAGES.EMPLOYER_NO_CLIPBOARD,
      }),

    workerTitle: z.string().trim().min(1, VALIDATION_MESSAGES.REQUIRED),

    referenceFirstName: z
      .string()
      .trim()
      .min(1, VALIDATION_MESSAGES.REQUIRED)
      .min(2, VALIDATION_MESSAGES.NAME_MIN_LENGTH)
      .regex(NAME_REGEX, VALIDATION_MESSAGES.NAME_INVALID_CHARS),

    referenceLastName: z
      .string()
      .trim()
      .min(1, VALIDATION_MESSAGES.REQUIRED)
      .min(2, VALIDATION_MESSAGES.NAME_MIN_LENGTH)
      .regex(NAME_REGEX, VALIDATION_MESSAGES.NAME_INVALID_CHARS),

    referencePhone: z
      .string()
      .trim()
      .min(1, VALIDATION_MESSAGES.REQUIRED)
      .refine((value) => validator.isMobilePhone(value, "en-US"), {
        message: VALIDATION_MESSAGES.INVALID_PHONE,
      }),

    referenceEmail: z
      .string()
      .trim()
      .min(1, VALIDATION_MESSAGES.REQUIRED)
      .refine((value) => validator.isEmail(value), {
        message: VALIDATION_MESSAGES.INVALID_EMAIL,
      }),

    employmentStartDate: z.preprocess(
      (value) => (value === null ? undefined : value),
      z
        .date({
          required_error: VALIDATION_MESSAGES.REQUIRED,
          invalid_type_error: VALIDATION_MESSAGES.INVALID_DATE,
        })
        .refine((date) => !isFuture(startOfDay(date)), {
          message: VALIDATION_MESSAGES.START_DATE_FUTURE,
        })
    ),

    employmentEndDate: z.preprocess(
      (value) => (value === null ? undefined : value),
      z
        .date({
          required_error: VALIDATION_MESSAGES.REQUIRED,
          invalid_type_error: VALIDATION_MESSAGES.INVALID_DATE,
        })
        .refine((date) => !isFuture(startOfDay(date)), {
          message: VALIDATION_MESSAGES.END_DATE_FUTURE,
        })
    ),
  })
  .refine(
    (data) => {
      return !isBefore(data.employmentEndDate, data.employmentStartDate);
    },
    {
      message: VALIDATION_MESSAGES.END_DATE_BEFORE_START,
      path: ["employmentEndDate"],
    }
  );

export type ReferenceFormSchema = z.infer<typeof referenceFormSchema>;

/**
 * API response schemas
 */
export const inviteMessageSchema = z.object({
  messageText: z.string(),
  webFormLink: z.string().url(),
});

const professionalReferenceStatusSchema = z.nativeEnum(PROFESSIONAL_REFERENCE_STATUS);

/**
 * IMPORTANT: Backend API inconsistency:
 * - POST expects 1-indexed months (1-12)
 * - GET returns 0-indexed months (0-11)
 */
export const professionalReferenceSchema = z.object({
  _id: z.string(),
  userId: z.string(),
  employerName: z.string(),
  workerTitle: z.string(),
  referenceFirstName: z.string(),
  referenceLastName: z.string(),
  fromMonth: z.number().int().min(0).max(11),
  fromYear: z.number().int(),
  toMonth: z.number().int().min(0).max(11),
  toYear: z.number().int(),
  status: professionalReferenceStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const professionalReferencesResponseSchema = z.object({
  references: z.array(professionalReferenceSchema),
  completed: z.boolean(),
  minVerifiedReferenceRequired: z.number().int().positive(),
  inviteMessage: inviteMessageSchema.optional(),
});

export const professionalReferenceResponseSchema = z.object({
  inviteMessage: inviteMessageSchema,
});

export const createProfessionalReferenceRequestSchema = z.object({
  employerName: z.string(),
  workerTitle: z.string(),
  referenceFirstName: z.string(),
  referenceLastName: z.string(),
  phoneNumber: z.string(),
  email: z.string(),
  fromMonth: z.number().int().min(1).max(12),
  fromYear: z.number().int(),
  toMonth: z.number().int().min(1).max(12),
  toYear: z.number().int(),
});

export const createProfessionalReferenceResponseSchema = z.object({
  data: z.object({
    _id: z.string(),
    userId: z.string(),
    employerName: z.string(),
    workerTitle: z.string(),
    periodStart: z.string(),
    periodEnd: z.string(),
    referenceFirstName: z.string(),
    referenceLastName: z.string(),
    status: professionalReferenceStatusSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
  inviteMessage: inviteMessageSchema,
});
